import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useCreateExport } from 'api/hooks/use-exports'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog,
  faWrench,
  faUserMinus,
  faBalanceScale,
  faDownload
} from '@fortawesome/free-solid-svg-icons'
import Dropdown from 'react-bootstrap/Dropdown'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import FieldLabel from 'shared/forms/field-label'
import ShieldLogo from 'assets/Shield/White/ThirdLine Shield White.svg'

// Each option queues a server-side export scoped to the current role. The
// `source` slug maps to the matching exporter in ExportWorker.
const EXPORT_OPTIONS = [
  {
    key: 'internal-conflicts',
    label: 'Internal Conflicts',
    source: 'roleInternalConflicts',
    prefix: 'Internal Conflicts'
  },
  {
    key: 'users',
    label: 'Users',
    source: 'roleEmployees',
    prefix: 'Users'
  },
  {
    key: 'permissions',
    label: 'Permissions',
    source: 'rolePermissions',
    prefix: 'Permissions'
  }
]

// Consolidates the role detail page's header actions — Manage Role, the
// analysis modals, and the per-table exports — into a single Actions
// dropdown matching the Users#show pattern. The Users and Permissions
// exports send the live tab filter state owned by show.jsx, so the CSV
// matches what's on screen.
function RoleActionsDropdown({
  role,
  onManageRole,
  setRoleModalShow,
  employeesParams,
  permissionsParams
}) {
  const [show, setShow] = useState(false)
  const [exportParams, setExportParams] = useState(null)

  const { mutate } = useCreateExport({
    params: exportParams,
    options: {
      onSuccess: () => toast.success('File will be available in Exports shortly')
    }
  })

  const filtersBySource = {
    roleEmployees: employeesParams,
    rolePermissions: permissionsParams
  }

  const handleSelect = (option) => {
    // Pagination keys are UI-only; everything else mirrors the tab's filters.
    const { page, perPage, ...filters } = filtersBySource[option.source] || {}
    setExportParams({
      name: `${option.prefix} - ${role.name}`,
      source: option.source,
      status: 'queued',
      params: { ...filters, role_id: role.id }
    })
    setShow(true)
  }

  const handleSubmit = () => {
    mutate()
    setShow(false)
    setExportParams(null)
  }

  return (
    <React.Fragment>
      <Dropdown className="d-inline-block ms-2">
        <Dropdown.Toggle variant="primary" id="role-actions-dropdown">
          <img
            src={ShieldLogo}
            alt=""
            className="me-2"
            style={{ height: '1.5em', verticalAlign: '-0.4em' }}
          />
          Actions
        </Dropdown.Toggle>
        <Dropdown.Menu align="end">
          <Dropdown.Item onClick={onManageRole}>
            <FontAwesomeIcon
              icon={faCog}
              fixedWidth
              className="me-2 text-muted"
            />
            Manage Role
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Item onClick={() => setRoleModalShow('remediation')}>
            <FontAwesomeIcon
              icon={faWrench}
              fixedWidth
              className="me-2 text-muted"
            />
            Remediation Analysis
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setRoleModalShow('change-permission')}>
            <FontAwesomeIcon
              icon={faUserMinus}
              fixedWidth
              className="me-2 text-muted"
            />
            Test Removal
          </Dropdown.Item>
          <Dropdown.Item onClick={() => setRoleModalShow('compare-roles')}>
            <FontAwesomeIcon
              icon={faBalanceScale}
              fixedWidth
              className="me-2 text-muted"
            />
            Compare Roles
          </Dropdown.Item>
          <Dropdown.Divider />
          <Dropdown.Header>
            <FontAwesomeIcon
              icon={faDownload}
              fixedWidth
              className="me-2 text-muted"
            />
            Export
          </Dropdown.Header>
          {EXPORT_OPTIONS.map((option) => (
            <Dropdown.Item key={option.key} onClick={() => handleSelect(option)}>
              {option.label}
            </Dropdown.Item>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <Modal show={show} onHide={() => setShow(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="lead fw-normal">
            <FontAwesomeIcon icon={faDownload} className="text-muted mx-2" />
            Export
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <FieldLabel>Export Name</FieldLabel>
            <Form.Control
              type="text"
              value={exportParams?.name || ''}
              autoFocus
              onChange={(e) =>
                setExportParams({ ...exportParams, name: e.target.value })
              }
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            className="bg-white text-muted"
            onClick={() => setShow(false)}
          >
            Close
          </Button>
          <Button variant="primary" onClick={() => handleSubmit()}>
            Export
          </Button>
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  )
}

RoleActionsDropdown.propTypes = {
  role: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string
  }).isRequired,
  onManageRole: PropTypes.func.isRequired,
  setRoleModalShow: PropTypes.func.isRequired,
  employeesParams: PropTypes.object,
  permissionsParams: PropTypes.object
}

export default RoleActionsDropdown
